import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isKeyOf<T extends Record<string, any>>(obj: T, key: string): key is Extract<keyof T, string> {
	return key in obj;
}

/**
 * returns a promise that resolves or times out after a given time
 */
export function timeoutPromise<T>(promise: Promise<T>, ms: number): Promise<T> {
	return new Promise((resolve, reject) => {
		const timeout = setTimeout(() => {
			reject(new Error(`Promise timed out after ${ms} ms`));
		}, ms);

		promise
			.then((result) => {
				clearTimeout(timeout);
				resolve(result);
			})
			.catch((error) => {
				clearTimeout(timeout);
				reject(error);
			});
	});
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type Listener<T> = (arg: T) => void;

export class TypedEventEmitter<Events extends Record<string, any>> {
	private listeners: {
		[K in keyof Events]?: Listener<Events[K]>[];
	} = {};

	on<K extends keyof Events>(event: K, listener: Listener<Events[K]>): () => void {
		if (!this.listeners[event]) {
			this.listeners[event] = [];
		}
		this.listeners[event]!.push(listener);

		return () => {
			this.off(event, listener);
		};
	}

	off<K extends keyof Events>(event: K, listener?: Listener<Events[K]>): void {
		if (!this.listeners[event]) return;

		if (listener) {
			this.listeners[event] = this.listeners[event]!.filter(
				(l) => l !== listener
			);
		} else {
			delete this.listeners[event];
		}
	}

	clear(): void {
		for (const event in this.listeners) {
			this.listeners[event as keyof Events] = [];
		}
	}

	once<K extends keyof Events>(event: K, listener: Listener<Events[K]>): () => void {
		const wrappedListener: Listener<Events[K]> = (arg) => {
			this.off(event, wrappedListener);
			listener(arg);
		};

		return this.on(event, wrappedListener);
	}

	emit<K extends keyof Events>(event: K, arg: Events[K]): void {
		const eventListeners = this.listeners[event];
		if (!eventListeners) return;

		for (const listener of eventListeners) {
			listener(arg);
		}
	}
}

export async function waitFor(
	condition: () => boolean,
	timeout: number,
	pollInterval: number = 100
): Promise<void> {
	return new Promise((resolve, reject) => {
		const startTime = Date.now();

		const checkCondition = () => {
			if (condition()) {
				resolve();
			} else if (Date.now() - startTime > timeout) {
				reject(new Error("Timeout waiting for condition"));
			} else {
				setTimeout(checkCondition, pollInterval);
			}
		};

		checkCondition();
	});
}

export function eventHandlers(
	...cleanupFunctions: (() => void)[]
): () => void {
	return () => {
		cleanupFunctions.forEach((cleanup) => cleanup());
	};
}